import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthProvider';
import { supabase } from '@/lib/supabase';

export type ResourceType = 'machine' | 'workstation';
export type ScheduleStatus =
  | 'draft'
  | 'planned'
  | 'released'
  | 'in_progress'
  | 'completed'
  | 'blocked'
  | 'cancelled';
export type SchedulePriority = 'low' | 'normal' | 'high' | 'urgent';

export interface WorkShift {
  id: string;
  work_center_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

export interface WorkResource {
  id: string;
  organization_id: string;
  code: string;
  name: string;
  resource_type: ResourceType;
  description: string | null;
  hourly_capacity: number;
  cost_per_hour_cents: number | null;
  efficiency_pct: number;
  daily_capacity_hours: number;
  color: string;
  location: string | null;
  is_active: boolean;
  work_center_shifts: WorkShift[];
}

export type ScheduleSource = 'manual' | 'reorder_point';

export interface ProductionSchedule {
  id: string;
  organization_id: string;
  /** Null only on drafts still waiting for a machine (migration 154). */
  work_center_id: string | null;
  work_order_id: string | null;
  /** Item this production order makes; open orders count toward its on-order quantity. */
  item_id: string | null;
  source: ScheduleSource;
  title: string;
  requirement: string | null;
  quantity: number;
  setup_minutes: number;
  run_minutes: number;
  planned_start: string;
  planned_end: string;
  actual_start: string | null;
  actual_end: string | null;
  actual_quantity: number | null;
  status: ScheduleStatus;
  priority: SchedulePriority;
  notes: string | null;
  created_at: string;
  updated_at: string;
  work_center: Pick<WorkResource, 'id' | 'code' | 'name' | 'resource_type' | 'color'> | null;
  item: { id: string; sku: string; name: string; base_uom: string } | null;
}

export interface ResourceInput {
  id?: string;
  code: string;
  name: string;
  resource_type: ResourceType;
  description?: string | null;
  location?: string | null;
  hourly_capacity: number;
  cost_per_hour_cents?: number | null;
  efficiency_pct: number;
  daily_capacity_hours: number;
  color: string;
  is_active: boolean;
  workingDays: number[];
  shiftStart: string;
  shiftEnd: string;
}

export interface ScheduleInput {
  id?: string;
  work_center_id: string | null;
  work_order_id?: string | null;
  item_id?: string | null;
  title: string;
  requirement?: string | null;
  quantity: number;
  setup_minutes: number;
  run_minutes: number;
  planned_start: string;
  planned_end: string;
  actual_start?: string | null;
  actual_end?: string | null;
  actual_quantity?: number | null;
  status: ScheduleStatus;
  priority: SchedulePriority;
  notes?: string | null;
}

export const SCHEDULE_KEYS = {
  resources: ['work-schedule-resources'] as const,
  schedules: ['production-schedules'] as const,
};

/** Production orders also feed the catalog's on-order / arrival columns and the item Inventory tab. */
function invalidateProductionOrders(queryClient: QueryClient) {
  return () => {
    queryClient.invalidateQueries({ queryKey: SCHEDULE_KEYS.schedules });
    queryClient.invalidateQueries({ queryKey: ['item-po-lines', 'on-order'] });
    queryClient.invalidateQueries({ queryKey: ['item-production-orders'] });
  };
}

export function useWorkResources() {
  return useQuery({
    queryKey: SCHEDULE_KEYS.resources,
    queryFn: async (): Promise<WorkResource[]> => {
      const { data, error } = await supabase
        .from('work_centers' as never)
        .select(
          'id, organization_id, code, name, resource_type, description, hourly_capacity, cost_per_hour_cents, efficiency_pct, daily_capacity_hours, color, location, is_active, work_center_shifts(id, work_center_id, day_of_week, start_time, end_time, is_active)',
        )
        .order('code');
      if (error) throw error;
      return ((data ?? []) as unknown as WorkResource[]).map((r) => ({
        ...r,
        hourly_capacity: Number(r.hourly_capacity),
        efficiency_pct: Number(r.efficiency_pct),
        daily_capacity_hours: Number(r.daily_capacity_hours),
        work_center_shifts: (r.work_center_shifts ?? []).sort(
          (a, b) => a.day_of_week - b.day_of_week,
        ),
      }));
    },
  });
}

export function useProductionSchedules() {
  return useQuery({
    queryKey: SCHEDULE_KEYS.schedules,
    queryFn: async (): Promise<ProductionSchedule[]> => {
      const { data, error } = await supabase
        .from('production_schedules' as never)
        .select(
          'id, organization_id, work_center_id, work_order_id, item_id, source, title, requirement, quantity, setup_minutes, run_minutes, planned_start, planned_end, actual_start, actual_end, actual_quantity, status, priority, notes, created_at, updated_at, work_center:work_centers(id, code, name, resource_type, color), item:items(id, sku, name, base_uom)',
        )
        .order('planned_start');
      if (error) throw error;
      return ((data ?? []) as unknown as ProductionSchedule[]).map((s) => ({
        ...s,
        quantity: Number(s.quantity),
        actual_quantity: s.actual_quantity == null ? null : Number(s.actual_quantity),
      }));
    },
  });
}

export function useSaveWorkResource() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResourceInput) => {
      if (!profile?.organization_id) throw new Error('No organization is selected.');
      const { id, workingDays, shiftStart, shiftEnd, ...fields } = input;
      const payload = { ...fields, organization_id: profile.organization_id };
      const query = id
        ? supabase.from('work_centers' as never).update(payload as never).eq('id', id)
        : supabase.from('work_centers' as never).insert(payload as never);
      const { data, error } = await query.select('id').single();
      if (error) throw error;
      const resourceId = (data as { id: string }).id;

      const { error: deleteError } = await supabase
        .from('work_center_shifts' as never)
        .delete()
        .eq('work_center_id', resourceId);
      if (deleteError) throw deleteError;
      if (workingDays.length) {
        const { error: shiftError } = await supabase.from('work_center_shifts' as never).insert(
          workingDays.map((day) => ({
            work_center_id: resourceId,
            day_of_week: day,
            start_time: shiftStart,
            end_time: shiftEnd,
          })) as never,
        );
        if (shiftError) throw shiftError;
      }
      return resourceId;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEDULE_KEYS.resources }),
  });
}

export function useSaveProductionSchedule() {
  const { profile, user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ScheduleInput) => {
      if (!profile?.organization_id) throw new Error('No organization is selected.');
      const { id, ...fields } = input;
      const payload = {
        ...fields,
        organization_id: profile.organization_id,
        ...(!id ? { created_by: user?.id ?? null } : {}),
      };
      const query = id
        ? supabase.from('production_schedules' as never).update(payload as never).eq('id', id)
        : supabase.from('production_schedules' as never).insert(payload as never);
      const { data, error } = await query.select('id').single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: invalidateProductionOrders(queryClient),
  });
}

/** Move a reviewed draft onto the schedule. The database refuses it if no machine is assigned. */
export function useConfirmProductionOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('production_schedules' as never)
        .update({ status: 'planned' } as never)
        .eq('id', id)
        .eq('status', 'draft');
      if (error) throw error;
    },
    onSuccess: invalidateProductionOrders(queryClient),
  });
}

export function useDeleteProductionSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('production_schedules' as never)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidateProductionOrders(queryClient),
  });
}

export function plannedMinutes(schedule: ProductionSchedule): number {
  return schedule.setup_minutes + schedule.run_minutes;
}

export function actualMinutes(schedule: ProductionSchedule): number | null {
  if (!schedule.actual_start || !schedule.actual_end) return null;
  return Math.max(
    0,
    Math.round(
      (Date.parse(schedule.actual_end) - Date.parse(schedule.actual_start)) / 60_000,
    ),
  );
}

export function hasScheduleConflict(
  candidate: Pick<ScheduleInput, 'id' | 'work_center_id' | 'planned_start' | 'planned_end'>,
  schedules: ProductionSchedule[],
): boolean {
  if (!candidate.work_center_id) return false;
  const start = Date.parse(candidate.planned_start);
  const end = Date.parse(candidate.planned_end);
  return schedules.some(
    (s) =>
      s.id !== candidate.id &&
      s.work_center_id === candidate.work_center_id &&
      s.status !== 'cancelled' &&
      Date.parse(s.planned_start) < end &&
      Date.parse(s.planned_end) > start,
  );
}
